/** @jsxRuntime classic */
/** @jsx jsx */
// eslint-disable-next-line
import { css, jsx } from '@emotion/react';
import { Fragment, JSX, MouseEvent, ReactNode, useMemo, useState } from 'react';
import {
  faChevronDown as iconDown,
  faChevronUp as iconUp,
  faLayerGroup as iconItem,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { ApiBookedItem, ApiBooking, ApiPrice } from '@ibe/api';
import {
  AnalyticsService,
  bookingItemRemovedEvent,
  createUniqueId,
  MaskBookingService,
  sanitize
} from '@ibe/services';
import { Button, Collapse } from 'reactstrap';
import { Price } from '../../UI/Price/Price';
import { defaultSummaryOrder, SummaryOrderKeyType } from '../../../../Models/ItemRegistry';
import Dates from '../../../Items/Dates';

export interface Props {
  title: ReactNode;
  subtitle?: string;
  description?: ReactNode;
  numberOfPerson?: string;
  additionalInfo?: string;
  price?: ApiPrice;
  children?: ReactNode;
  icon?: ReactNode;
  isOpen?: boolean;
  dates?: ReactNode;
  onDelete?: (idItem: string) => Promise<void>;
  booking?: ApiBooking;
  item?: ApiBookedItem;
  withIconColumn?: boolean;
  hideSinglePrices?: boolean;
  order?: SummaryOrderKeyType[];
  relatedItems?: ApiBookedItem[];
  showPricePlusMinus?: boolean;
}

const SummaryItem = (props: Props): JSX.Element => {
  const defaultIcon = <Icon icon={iconItem} />;
  const {
    title,
    subtitle,
    description,
    additionalInfo,
    numberOfPerson,
    price,
    children,
    icon = defaultIcon,
    isOpen: isOpenVal,
    dates,
    onDelete,
    booking,
    item,
    withIconColumn,
    hideSinglePrices,
    order = defaultSummaryOrder,
    relatedItems,
    showPricePlusMinus
  } = props;
  const [isOpen, setIsOpen] = useState(!!isOpenVal);
  const clickableIcon = css`
    cursor: pointer;
    opacity: 0.75;

    &:hover {
      opacity: 1;
    }
  `;
  const keys: string[] = useMemo(() => order.map(() => createUniqueId()), []);

  const renderTitle = () => {
    return <h5 className="card-title m-0 mb-1">{title}</h5>;
  };

  const renderDeleteButton = () => {
    return (
      <div className="text-right h5 m-0 summary__delete">
        <div className="px-1" style={{ margin: '4px 0 0 0' }}>
          <Button
            outline
            color="primary"
            onClick={(e: MouseEvent<HTMLButtonElement>): void => {
              e.stopPropagation();
              if (booking && item && onDelete) {
                onDelete(item.id);
                bookingItemRemovedEvent.broadcast({
                  booking: MaskBookingService.maskBooking(booking),
                  item: null,
                  analyticsItem: AnalyticsService.createItem(item, booking)
                });
              }
            }}
          >
            <FontAwesomeIcon css={clickableIcon} icon={faTrashAlt} />
          </Button>
        </div>
      </div>
    );
  };

  const renderTitleDeleteButton = () => {
    return (
      <div className="row">
        <h5 className="col card-title m-0 mb-1">{title}</h5>
        {onDelete && (
          <div className="col flex-grow-0 h5 m-0 summary__delete">
            <div className="px-1" style={{ margin: '4px 0 0 0' }}>
              <Button
                outline
                color="primary"
                onClick={(e: MouseEvent<HTMLButtonElement>): void => {
                  e.stopPropagation();
                  if (booking && item && onDelete) {
                    onDelete(item.id);
                    bookingItemRemovedEvent.broadcast({
                      booking: MaskBookingService.maskBooking(booking),
                      item: null,
                      analyticsItem: AnalyticsService.createItem(item, booking)
                    });
                  }
                }}
              >
                <FontAwesomeIcon css={clickableIcon} icon={faTrashAlt} />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSubTitle = () => {
    return (
      <Fragment>
        {subtitle && !withIconColumn && (
          <h6 className="card-subtitle m-0 text-muted iso__summaryItem-subtitle">
            <span className="small">{icon}</span> {subtitle}
          </h6>
        )}
      </Fragment>
    );
  };

  const renderDate = () => {
    return (
      <Fragment>
        {dates && <div className="text-muted iso__summaryItem-dates">{dates}</div>}

        {relatedItems &&
          relatedItems.map(relatedItem => {
            return <Dates item={relatedItem} />;
          })}
      </Fragment>
    );
  };

  const renderDescription = () => {
    if (typeof description === 'string') {
      return (
        <Fragment>
          {description && (
            <div
              className="text-muted iso__summary__description"
              dangerouslySetInnerHTML={{ __html: sanitize(description) }}
            />
          )}
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          {description && <div className="text-muted iso__summary__description">{description}</div>}
        </Fragment>
      );
    }
  };

  const renderAdditionalInfo = () => {
    return (
      <Fragment>
        {additionalInfo && (
          <div
            className="text-muted iso__summary__additionalInfo"
            dangerouslySetInnerHTML={{ __html: sanitize(additionalInfo) }}
          />
        )}
      </Fragment>
    );
  };

  const renderPriceIcon = (renderPrice: boolean, renderIcon: boolean) => {
    return (
      <div className="d-flex align-items-center justify-content-end iso__summary__price__container">
        {renderPrice && price && !hideSinglePrices && (
          <p className="m-0">
            <Price
              className="price"
              showReduction
              showModifiers
              showTaxes={false}
              price={price}
              prefix={
                showPricePlusMinus && price.finalPrice >= 0
                  ? '+'
                  : showPricePlusMinus && price.finalPrice < 0
                  ? '-'
                  : undefined
              }
            />
          </p>
        )}
        {renderIcon && children && <Icon className="ml-1" icon={isOpen ? iconUp : iconDown} />}
      </div>
    );
  };

  const renderNumberPerson = () => {
    return (
      <Fragment>
        {numberOfPerson && (
          <div
            className="text-muted iso__summary__number__person"
            dangerouslySetInnerHTML={{ __html: sanitize(numberOfPerson) }}
          />
        )}
      </Fragment>
    );
  };

  const renderCardBody = () => {
    return (
      <div className={`align-items-center${withIconColumn ? ' flex-grow-1' : ''}`}>
        <div className="flex-grow-1">
          {order.map((orderItem, orderItemIndex) => {
            return (
              <Fragment key={keys[orderItemIndex]}>
                {orderItem === 'TITLE' ? renderTitle() : null}
                {orderItem === 'TITLE_DELETE' ? renderTitleDeleteButton() : null}
                {orderItem === 'DELETE' ? renderDeleteButton() : null}
                {orderItem === 'SUBTITLE' ? renderSubTitle() : null}
                {orderItem === 'DATE' ? renderDate() : null}
                {orderItem === 'DESCRIPTION' ? renderDescription() : null}
                {orderItem === 'ADDITIONAL_INFO' ? renderAdditionalInfo() : null}
                {orderItem === 'PRICE_AND_ICON' ? renderPriceIcon(true, true) : null}
                {orderItem === 'ICON' ? renderPriceIcon(false, true) : null}
                {orderItem === 'PRICE' ? renderPriceIcon(true, false) : null}
                {orderItem === 'NUMBER_OF_PERSON' ? renderNumberPerson() : null}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`iso__summaryItem  iso__summaryItem__${item?.itemType}`}>
      <div className="card" style={{ cursor: children ? 'pointer' : 'default', minWidth: '200px' }}>
        <div className="card-body" onClick={(): void => setIsOpen(!isOpen)}>
          {withIconColumn ? (
            <div className="d-flex">
              <div className="card-icon-column mr-2">{icon}</div>
              {renderCardBody()}
            </div>
          ) : (
            renderCardBody()
          )}
        </div>
        {children && (
          <Collapse isOpen={isOpen}>
            <div className="card-footer text-muted">{children}</div>
          </Collapse>
        )}
      </div>
    </div>
  ) as JSX.Element;
};
export default SummaryItem;
